import { useEffect, useState } from "react"
import { Avatar, Badge, Tooltip } from "@mui/material"

import http from "../api/http"
import { avatarColor, initialsOf } from "../utils/format"

/**
 * A person, as a face or as their initials.
 *
 * ── Why the image is fetched rather than linked ─────────────────────────────
 *
 * The file route authorises every byte it serves, so it needs the
 * Authorization header — and an `<img src>` carries no headers. The
 * alternatives are worse: a token in the query string puts a credential in
 * browser history and every proxy log between here and the server, and an
 * unauthenticated avatar endpoint makes the user id a lookup key for a photo of
 * the person who holds it.
 *
 * ── Why there is a cache and why it is module-level ─────────────────────────
 *
 * A chat thread shows the same six faces two hundred times. Without a cache
 * that is two hundred requests and two hundred blobs held in memory, and the
 * cache has to outlive the component or scrolling a list re-fetches everything
 * that just scrolled off. Keyed by the avatar key, so a person who changes
 * their photo gets a new key and misses the old entry.
 *
 * In-flight requests are cached too, not just results: mounting forty avatars
 * for one person in the same tick would otherwise fire forty identical
 * requests before the first one lands.
 */
const cache = new Map()

function loadAvatar(key) {
  if (cache.has(key)) return cache.get(key)
  const request = http
    .get(`/uploads/${key}`, { responseType: "blob" })
    .then((response) => URL.createObjectURL(response.data))
    .catch(() => {
      /* Cached as a failure rather than deleted, so a deleted file or a
         revoked permission does not re-request on every render forever. The
         face falls back to initials, which is a complete answer. */
      cache.set(key, null)
      return null
    })
  cache.set(key, request)
  return request
}

/** Dropped when somebody changes their own photo, so the new one is fetched. */
export function forgetAvatar(key) {
  if (key) cache.delete(key)
}

export default function UserAvatar({
  name,
  email,
  avatarKey,
  size = 32,
  /** A dot on the corner — presence, or anything else worth one glyph. */
  badge = null,
  tooltip = true,
  sx,
}) {
  const [src, setSrc] = useState(null)
  const label = name || email || "?"

  useEffect(() => {
    if (!avatarKey) {
      setSrc(null)
      return undefined
    }
    let live = true
    Promise.resolve(loadAvatar(avatarKey)).then((url) => {
      if (live) setSrc(url || null)
    })
    return () => {
      live = false
    }
  }, [avatarKey])

  const avatar = (
    <Avatar
      src={src || undefined}
      alt={label}
      sx={{
        width: size,
        height: size,
        fontSize: Math.max(10, Math.round(size * 0.4)),
        fontWeight: 700,
        /* The colour is derived from the name, so the same person is the same
           colour on every screen — which is most of what makes initials
           recognisable at all. Only behind initials: a photo needs no tint. */
        bgcolor: src ? "transparent" : avatarColor(label),
        color: "#fff",
        ...sx,
      }}
    >
      {initialsOf(label)}
    </Avatar>
  )

  const wrapped = badge ? (
    <Badge
      overlap="circular"
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      badgeContent={badge}
      sx={{ "& .MuiBadge-badge": { minWidth: 10, height: 10, p: 0, borderRadius: "50%" } }}
    >
      {avatar}
    </Badge>
  ) : (
    avatar
  )

  return tooltip ? <Tooltip title={label}>{wrapped}</Tooltip> : wrapped
}
